import React, { useEffect, useState } from "react";
import { Lock, ShieldCheck, Hammer } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const LS_KEY = "abs_gate_unlocked_v1";

/**
 * SiteGate — wraps the entire app. While the trustee board has the gate
 * "enabled", any visitor without a valid cookie/login sees a branded
 * "Coming Soon" page with an access-code field.
 *
 *  Bypass logic:
 *   - Logged-in admin/trustee → bypass (backend says locked:false).
 *   - Backend cookie `abs_gate=1` → bypass.
 *   - Local fallback: `localStorage[abs_gate_unlocked_v1] === "1"` keeps the
 *     user unlocked across sessions even if cookies get cleared inadvertently.
 */
export default function SiteGate({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(null); // null = loading
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refreshStatus = async () => {
    try {
      const r = await api.get("/site-gate/status");
      setStatus(r.data);
    } catch (e) {
      // If status check fails, fail OPEN (don't trap users if backend is down)
      setStatus({ locked: false, reason: "status_error" });
    }
  };

  useEffect(() => {
    if (authLoading) return;
    // Quick local check — saves a roundtrip flash if we previously unlocked
    if (typeof window !== "undefined" && window.localStorage.getItem(LS_KEY) === "1") {
      setStatus({ locked: false, reason: "local_unlock" });
      return;
    }
    refreshStatus();
  }, [authLoading, user]);

  // Print/poster pages must render without the gate (used by
  // capture_posters.py and shareable print URLs).
  if (typeof window !== "undefined") {
    const path = window.location.pathname;
    if (path.startsWith("/poster/") || path.startsWith("/launch/")) {
      return children;
    }
  }

  if (status === null) {
    return (
      <div className="fixed inset-0 bg-navy flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-saffron border-t-transparent animate-spin" />
      </div>
    );
  }

  if (!status.locked) return children;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/site-gate/unlock", { password: pw });
      window.localStorage.setItem(LS_KEY, "1");
      // Re-check status from backend so admin trustee toggle is also respected
      await refreshStatus();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not unlock");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] bg-navy text-pearl flex items-center justify-center px-4 py-8 overflow-y-auto"
      data-testid="site-gate"
    >
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 80% 15%, rgba(207,160,82,0.55) 0%, transparent 55%), radial-gradient(circle at 15% 85%, rgba(207,160,82,0.18) 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-md w-full">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-saffron/15 border border-saffron/40 text-saffron rounded-full text-[10px] uppercase tracking-[0.3em] font-bold">
            <Hammer size={12} /> Private alpha
          </div>
          <h1 className="font-display text-4xl sm:text-5xl mt-5 leading-tight">
            We're still building.
          </h1>
          <p className="mt-4 text-pearl/75 font-serif leading-relaxed">
            The Ambedkarite Buddhist Global Network UK website is currently in
            private alpha as we prepare for our Charity Commission
            application and our public launch. If you have been given an
            access code, please enter it below.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="mt-8 bg-white/5 backdrop-blur-sm border border-white/15 rounded-xl p-5"
          data-testid="site-gate-form"
        >
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-pearl/65 mb-2 flex items-center gap-1.5">
              <Lock size={11} /> Access code
            </span>
            <input
              type="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              autoFocus
              autoComplete="off"
              className="w-full px-4 py-3 bg-navy/40 border border-pearl/20 rounded-md text-pearl placeholder-pearl/35 focus:outline-none focus:border-saffron"
              placeholder="Enter the code you were given"
              data-testid="site-gate-password"
            />
          </label>
          {error && (
            <p className="mt-3 text-xs text-rose-300 flex items-center gap-1.5" data-testid="site-gate-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy || !pw}
            data-testid="site-gate-submit"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-saffron text-navy rounded-md text-sm font-medium hover:bg-saffron-light disabled:opacity-40"
          >
            <ShieldCheck size={15} /> {busy ? "Unlocking…" : "Enter the site"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-[11px] text-pearl/55">
            For access requests, please contact the trustees at{" "}
            <a
              href="mailto:info@ambedkaritebuddhistglobalnetworkuk.com"
              className="text-saffron hover:text-saffron-light underline"
            >
              info@ambedkaritebuddhistglobalnetworkuk.com
            </a>
            .
          </p>
          <p className="mt-3 text-[10px] text-pearl/35 italic">
            "Educate, Agitate, Organise" — Dr. B. R. Ambedkar
          </p>
        </div>
      </div>
    </div>
  );
}
